import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { useAllUsers } from 'context/AllUserDataContext';
import useConfirmationDialog from 'routes/Pages/components/ConfirmDialogs/useConfirmationDialog';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import { fromApi, toApi } from '../utils/requisitionMapper';
import { useSendContractNotification } from 'routes/Pages/TimberSalesPricingList/CustomHooks/useSendContractNotification';
import { formatDieselOrderEmailHTML } from 'notification_templates/DieselOrderedNotification';
import moment from 'moment';

// "Other" has no dedicated backend field — fold the typed free-text name
// into supplierName before it goes anywhere near the API payload.
const resolveSupplierName = formValues =>
  formValues.supplierName === 'Other' ? formValues.otherSupplierName?.trim() : formValues.supplierName;

const useRequisition = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { getAllUsers, allUsers } = useAllUsers();
  const { sendNotification } = useSendContractNotification();
  const isMounted = useRef(true);

  // ─── Grid / page state ────────────────────────────────────────────────────
  const [purchaseReqData, setPurchaseReqData] = useState([]);
  const [currentTabPurchaseReqData, setCurrentTabPurchaseReqData] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);

  // ─── Modal state ──────────────────────────────────────────────────────────
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);

  // ─── Uploaded docs (existing server files shown in edit mode) ────────────
  const [uploadedDocs, setUploadedDocs] = useState({ file: null, invoice: null });

  // ─── SP list (Diesel Purchase only) ──────────────────────────────────────
  const [spList, setSpList] = useState([]);

  const [supplierList, setSupplierList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);

  const {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,
  } = useConfirmationDialog();

  // ─── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    fetchRequisitions();
    getAllUsers();
    fetchSpList();
    fetchSuppliers();
    fetchDepartments();
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ─── Grid data ────────────────────────────────────────────────────────────
  const fetchRequisitions = () => {
    dispatch({ type: FETCH_START });
    customAxios
      .get('/PO/GetRequisitions', { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          const mapped = res.data.map(fromApi);
          console.log('mapped ===', mapped);
          setPurchaseReqData(mapped);
          filterByTab(currentTab, mapped);
        } else {
          showToast('Failed to load Purchase Requisition data', 'error');
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(() => {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  const filterByTab = (tabVal, data) => {
    let filtered = [];
    if (tabVal === 0) {
      filtered = data;
    } else if (tabVal === 1) {
      filtered = data?.filter(d => d.purchaseOrReturn === 'Purchase' && d.requisitionStatus && d.originatedBy);
    } else if (tabVal === 2) {
      filtered = data.filter(
        d =>
          d.purchaseOrReturn === 'Diesel Purchase' &&
          (d.requisitionStatus === 'Requested' || d.requisitionStatus === 'Ordered' || d.requisitionStatus === ''),
      );
    } else if (tabVal === 3) {
      filtered = data.filter(d => d.purchaseOrReturn === 'Recharge');
    } else if (tabVal === 4) {
      filtered = data;
    } else if (tabVal === 5) {
      filtered = data;
    }
    setCurrentTabPurchaseReqData(filtered);
  };

  const supplierListRef = useRef([]);

  const fetchSuppliers = () =>
    customAxios
      .get('/PO/GetSupplierDetails', { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          setSupplierList(res.data);
          supplierListRef.current = res.data;
          return res.data;
        }
        return [];
      })
      .catch(() => {
        setSupplierList([]);
        supplierListRef.current = [];
        return [];
      });

  const fetchDepartments = () => {
    customAxios
      .get('/Ops/GetMappingDepartments', { headers: authHeader() })
      .then(res => {
        if (res.status === 200) setDepartmentList(res.data);
      })
      .catch(() => setDepartmentList([]));
  };

  const handleTabChange = (event, newTab) => {
    setCurrentTab(newTab);
    filterByTab(newTab, purchaseReqData);
  };

  // ─── Modal open/close ─────────────────────────────────────────────────────
  const handleAddNew = () => {
    setIsEdit(false);
    setSelectedReq(null);
    setUploadedDocs({ file: null, invoice: null });
    setOpenModal(true);
  };

  const normalize = s => (s || '').trim().toLowerCase();

  const handleOpenEdit = async reqData => {
    setIsEdit(true);

    // Make sure we have the supplier list before deciding "Other" vs known
    let suppliers = supplierListRef.current;
    if (!suppliers.length) {
      suppliers = await fetchSuppliers();
    }

    const savedName = reqData.supplierName;
    const match = suppliers.find(s => normalize(s.supplierName) === normalize(savedName));

    let supplierFields = {};
    if (savedName) {
      if (match) {
        // Use the canonical name so the dropdown value matches an option exactly
        supplierFields = { supplierName: match.supplierName };
      } else if (suppliers.length > 0) {
        // Only treat as "Other" when we actually had a list to compare against
        supplierFields = { otherSupplierName: savedName, supplierName: 'Other' };
      }
    }

    setSelectedReq({ ...reqData, column: '', ...supplierFields });
    setOpenModal(true);
    fetchReqDocs(reqData.poReqNumber);
  };

  // ─── Existing docs (edit mode) ────────────────────────────────────────────
  const fetchReqDocs = poReqNumber => {
    if (!poReqNumber) return;
    customAxios
      .get(`/PO/GetRequisitionsDocs?pO_ReqNumber=${poReqNumber}`, { headers: authHeader() })
      .then(res => {
        if (res.status === 200 && res.data.length > 0) {
          const grouped = res.data.reduce(
            (acc, doc) => {
              const key = doc.docType === 'files' ? 'file' : 'invoice';
              acc[key] = acc[key] ? [...acc[key], doc] : [doc];
              return acc;
            },
            { file: null, invoice: null },
          );
          setUploadedDocs(grouped);
        } else {
          setUploadedDocs({ file: null, invoice: null });
        }
      })
      .catch(() => {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  const handleFileDelete = fileData => {
    handleOpenConfirmationDialog(
      'This will delete the selected file. Are you sure?',
      () => {
        dispatch({ type: FETCH_START });
        customAxios
          .post('/PO/DeleteRequisitionsDocs', { id: fileData.id }, { headers: authHeader() })
          .then(res => {
            if (res.status === 200) {
              showToast('File deleted successfully.', 'success');
              fetchReqDocs(selectedReq?.poReqNumber);
            }
            dispatch({ type: FETCH_SUCCESS });
          })
          .catch(() => {
            dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
          });
      },
      null,
    );
  };

  const viewFile = fileData => {
    dispatch({ type: FETCH_START });
    customAxios
      .get(`/PO/GetRequisitionsFile?id=${fileData.id}`, {
        headers: authHeader(),
        responseType: 'blob',
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileData.documentName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(() => {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  // ─── SP list (Diesel) ─────────────────────────────────────────────────────
  const fetchSpList = () => {
    dispatch({ type: FETCH_START });
    customAxios
      .get('/Ops/GetContracts', { headers: authHeader() })
      .then(res => {
        const unique = res.data.filter(sp => sp.wH_NumberName);
        setSpList(unique);
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(() => {
        setSpList([]);
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      });
  };

  // ─── Submit helpers ───────────────────────────────────────────────────────
  const uploadDocs = (poReqNumber, files, docType) => {
    if (!files || files.length === 0) return;
    const formData = new FormData();
    formData.append('pO_ReqNumber', poReqNumber);
    formData.append('docType', docType);
    files.forEach((file, i) => formData.append(`file[${i}]`, file));
    customAxios
      .post('/PO/AddRequistionsDocs', formData, { headers: authHeader() })
      .catch(() => showToast('Failed to upload files', 'error'));
  };

  // ─── New submit ───────────────────────────────────────────────────────────
  const handleSubmit = formValues => {
    const { files, invoiceFiles } = formValues;

    // ─── Validation (kept above FETCH_START so a failed check never leaves the loader stuck) ───
    if (formValues.purchaseOrReturn === 'Diesel Purchase') {
      const litresOrdered = formValues.litresOrdered;
      if (litresOrdered === null || litresOrdered === undefined || litresOrdered === '' || Number(litresOrdered) <= 0) {
        showToast('Litres Ordered is mandatory when adding a Diesel Purchase requisition', 'error');
        return;
      }
    }

    if (formValues.purchaseOrReturn === 'Purchase' && !formValues.description?.trim()) {
      showToast('Description is mandatory for a Purchase requisition', 'error');
      return;
    }

    if (formValues.supplierName === 'Other' && !formValues.otherSupplierName?.trim()) {
      showToast('Please enter the supplier name', 'error');
      return;
    }

    dispatch({ type: FETCH_START });
    const payload = toApi({ ...formValues, supplierName: resolveSupplierName(formValues) }, false);

    customAxios
      .post('/PO/AddRequisitions', payload, { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          showToast('Purchase Requisition added successfully', 'success');
          if (files) uploadDocs(res.data.newPOReqNumber, files, 'files');
          if (invoiceFiles) uploadDocs(res.data.newPOReqNumber, invoiceFiles, 'invoiceFiles');
          fetchRequisitions();
          setOpenModal(false);
          setSelectedReq(null);
        } else {
          showToast('Failed to add Purchase Requisition', 'error');
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(err => {
        showToast('Failed to add Purchase Requisition', 'error');
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        throw err;
      });
  };

  // ─── Edit submit ──────────────────────────────────────────────────────────
  const isValidNonNegativeNumber = value => {
    if (value === null || value === undefined || value === '') return false;
    const num = Number(value);
    return !isNaN(num) && isFinite(num) && num >= 0;
  };

  const isValidPositiveNumber = value => {
    if (value === null || value === undefined || value === '') return false;
    const num = Number(value);
    return !isNaN(num) && isFinite(num) && num > 0;
  };

  const handleEdit = formValues => {
    // ─── Cancelled: description is mandatory to justify the cancellation ────
    if (formValues.requisitionStatus === 'Cancelled' && !formValues.description?.trim()) {
      showToast('Description is mandatory when status is Cancelled', 'error');
      return;
    }

    // ─── Purchase: description is always mandatory ──────────────────────────
    if (formValues.purchaseOrReturn === 'Purchase' && !formValues.description?.trim()) {
      showToast('Description is mandatory for a Purchase requisition', 'error');
      return;
    }

    // ─── Supplier "Other" ─────────────────────────────────────────────────
    if (formValues.supplierName === 'Other' && !formValues.otherSupplierName?.trim()) {
      showToast('Please enter the supplier name', 'error');
      return;
    }

    // ─── Purchase ─────────────────────────────────────────────────────────
    if (formValues.purchaseOrReturn === 'Purchase' && formValues.requisitionStatus === 'Approved') {
      const hasNewInvoiceFiles = formValues.invoiceFiles?.length > 0;
      const hasExistingInvoiceFiles = uploadedDocs?.invoice?.length > 0;

      if (!hasNewInvoiceFiles && !hasExistingInvoiceFiles) {
        showToast('Invoice upload is mandatory when status is Approved', 'error');
        return;
      }
      if (!isValidNonNegativeNumber(formValues.totalNetValue)) {
        showToast('Total Net Value must be a valid number, 0 or above, when status is Approved', 'error');
        return;
      }
    }

    // ─── Diesel Purchase ──────────────────────────────────────────────────
    if (formValues.purchaseOrReturn === 'Diesel Purchase') {
      if (formValues.requisitionStatus === 'Ordered') {
        if (!formValues.supplierName?.trim()) {
          showToast('Supplier Name is mandatory when status is Ordered', 'error');
          return;
        }
      }

      if (formValues.requisitionStatus === 'Approved') {
        console.log('formValues.litresDelivered ===', formValues.litresDelivered);
        if (!isValidPositiveNumber(formValues.litresDelivered)) {
          showToast('Litres Delivered is required when Requisition Status is Approved.', 'error');
          return;
        }
        if (!isValidNonNegativeNumber(formValues.totalNetValue)) {
          showToast('Total Net Value must be a valid number, 0 or above, when Requisition Status is Approved.', 'error');
          return;
        }

        const hasNewFiles = formValues.files?.length > 0;
        const hasExistingFiles = uploadedDocs?.file?.length > 0;
        if (!hasNewFiles && !hasExistingFiles) {
          showToast('File upload is mandatory when status is Approved', 'error');
          return;
        }

        const hasNewInvoiceFiles = formValues.invoiceFiles?.length > 0;
        const hasExistingInvoiceFiles = uploadedDocs?.invoice?.length > 0;
        if (!hasNewInvoiceFiles && !hasExistingInvoiceFiles) {
          showToast('Invoice upload is mandatory when status is Approved', 'error');
          return;
        }
      }
    }

    dispatch({ type: FETCH_START });
    const { files, invoiceFiles } = formValues;

    console.log("FORM VALUES ====", formValues);
    const payload = toApi(
      { ...selectedReq, ...formValues, supplierName: resolveSupplierName(formValues) },
      true,
    );

    customAxios
      .post('/PO/EditRequisitions', payload, { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          showToast('Purchase Requisition edited successfully', 'success');
          if (files) uploadDocs(payload.poReqNumber, files, 'files');
          if (invoiceFiles) uploadDocs(payload.poReqNumber, invoiceFiles, 'invoiceFiles');
          handleNotification(payload);
          fetchRequisitions();
          setOpenModal(false);
          setSelectedReq(null);
          setIsEdit(false);
        } else {
          showToast('Failed to edit Purchase Requisition', 'error');
        }
        dispatch({ type: FETCH_SUCCESS });
      })
      .catch(err => {
        showToast('Failed to edit Purchase Requisition', 'error');
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        throw err;
      });
  };

  // Send notificatio
  const handleNotification = payload => {
    if (payload.purchaseOrReturn === 'Diesel Purchase' && payload.requisitionStatus === 'Ordered') {
      const emailDetails = {
        spName: payload.spName,
        supplierName: payload.supplierName,
        litres: payload.litresOrdered || 0,
        orderedAt: moment().format('lll'),
        location: payload.fuelLocationDropPin || 'N/A',
      };

      const html = formatDieselOrderEmailHTML(payload.poReqNumber, emailDetails);

      const driverEmail = payload.originatedBy;

      console.log('EMAIL DETAILS', emailDetails);
      console.log('DRIVER EMAIL ===', driverEmail);

      if (driverEmail) {
        sendNotification({
          sendTo: 'ashish.garg@dscission.com', //driverEmail,
          subject: `Diesel Order Assigned - PO #${payload.poReqNumber}`,
          body: html,
          deliveryType: 'EMAIL',
          primaryKey: null,
          uniqueRef: payload.poReqNumber,
          templateType: null,
          notificationSection: 'PO',
        });
      } else {
        showToast('Driver email not found. Notification could not be sent.', 'warning');
      }
    }
  };

  return {
    // Grid
    purchaseReqData,
    currentTabPurchaseReqData,
    currentTab,
    handleTabChange,
    // Modal
    openModal,
    setOpenModal,
    isEdit,
    selectedReq,
    handleAddNew,
    handleOpenEdit,
    // Form submit
    handleSubmit,
    handleEdit,
    // Docs
    uploadedDocs,
    handleFileDelete,
    viewFile,
    // SP list
    spList,
    // Users
    allUsers,
    // Confirmation dialog
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    closeConfirmationDialog,

    supplierList,
    departmentList,
  };
};

export default useRequisition;